import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SimpleCodeValue } from '../../../core/models/code-value.model';
import { ManageComplaintService } from '../../../core/services/manage-complaints.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ReportService } from '../../../core/services/report.service';
import { AuthContext } from '../../../auth/services/auth.context';

@Component({
  selector: 'app-complaints-summary',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    MatSnackBarModule
  ],
  templateUrl: './complaints-summary.component.html',
  styleUrls: ['./complaints-summary.component.scss'],
  providers: [provideNativeDateAdapter()],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ComplaintsSummaryComponent implements OnInit {
  summaryForm = new FormGroup({
    departmentId: new FormControl(null, Validators.required),
    startDate: new FormControl<Date | null>(null, Validators.required),
    endDate: new FormControl<Date | null>(null, Validators.required),
    restrictResults: new FormControl(false),
  });

  departments: SimpleCodeValue[] = [];
  isLoading = false;
  isToggleVisible = false;

  constructor(
    private manageComplaintService: ManageComplaintService,
    private reportService: ReportService,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private authContext: AuthContext
  ) {}

  ngOnInit(): void {
    const userRole = this.authContext.getUserInfo().userType;
    if (userRole === 'ROLE_ADMIN' || userRole === 'ROLE_GE' || userRole === 'ROLE_AGE') {
      this.isToggleVisible = true;
    }

    this.getDepartments();
  }

  private getDepartments(): void {
    this.manageComplaintService.getAccessibleDepartments().subscribe({
        next: (response) => {
          this.departments = response;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error('Error loading departments:', error);
        }
    });
  }

  onReset(): void {
    this.summaryForm.reset({
      departmentId: null,
      startDate: null,
      endDate: null,
      restrictResults: false,
    });
  }

  isFormValid(): boolean {
    return this.summaryForm.valid;
  }

  generateSummaryReport(): void {
    if (!this.isFormValid()) {
      this.snackBar.open('Please select at least one filter', 'Close', {
        duration: 3000,
      });
      return;
    }

    const formValue = this.summaryForm.value;
    const params: any = {};

    if (formValue.departmentId) {
      params.departmentId = formValue.departmentId;
    }

    if (formValue.startDate) {
      params.startDate = this.formatDate(formValue.startDate as Date);
    }

    if (formValue.endDate) {
      params.endDate = this.formatDate(formValue.endDate as Date);
    }

    if (this.isToggleVisible) {
      params.restrictResults = !!formValue.restrictResults;
    } else {
      params.restrictResults = false;
    }

    this.isLoading = true;
    const reportName = 'CountSummaryGE';

    this.reportService.downloadReport(reportName, params).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        this.downloadPDF(response, reportName);
        this.snackBar.open('Complaints summary generated', 'Close', {
          duration: 3000,
        });
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.isLoading = false;
        console.error('Error generating complaints summary report:', error);
        this.snackBar.open('Error generating report. Please try again.', 'Close', {
          duration: 3000,
        });
        this.cdr.detectChanges();
      },
    });
  }

  private downloadPDF(blob: any, reportName: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${reportName}_${new Date().getTime()}.pdf`;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}